/*
 * PAST (direction §11).
 *
 * THE ORGANISING FACT: "this view grows forever." Everything below follows from
 * it — all groups collapsed by default, search on the server, reverse-chronological
 * order. A screen that is fine with two tournaments and unusable with two hundred
 * is a screen that fails exactly when the product has succeeded.
 *
 * THE COLLAPSE PATTERN (§11): "The band is a full-width button (aria-expanded),
 * tap anywhere to toggle, red chevron as the interactive marker — safe here because
 * Past bands carry no links (pills do)."
 *
 * That parenthetical is the load-bearing part. Making a whole band a button is
 * normally a principle-1 violation waiting to happen, because a button swallows
 * any link inside it. It is safe HERE and only here: Past's band carries no
 * capability pill, so there is no link to swallow. Adding one to a band would
 * break this pattern, not merely crowd it.
 *
 * WHAT §11 RULES OUT BY NAME:
 *   - No best-result surfacing. It editorializes — the app does not decide which
 *     of a fencer's results mattered.
 *   - No "completed" badges. Principle 8: everything here is completed by
 *     definition, so the badge would be on every row and carry nothing.
 *   - Scratched fencers never appear. Filtered in SQL, one layer below this, on
 *     the grounds that a scratch on the wire is one bug away from rendering.
 *   - Weight carries the celebration for a placement — "no color, no serif".
 */

import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { EventSubHeader } from "../components/Band";
import { CapabilityPill } from "../components/CapabilityPill";
import { Card, CardRow } from "../components/Card";
import { SegmentedToggle, type GroupBy } from "../components/SegmentedToggle";
import { TournamentFlag } from "../components/TournamentFlag";
import type { Schemas } from "../lib/api";
import { formatLocation } from "../lib/country";
import { dateLabel, dateLocale, dateRange, firstLast } from "../lib/format";
import { regroup } from "../lib/regroup";
import { fencerCounts, tournamentCounts } from "../lib/summary";
import { isTeamRow, teamNames } from "../lib/team";
import { TeamLine } from "../components/TeamLine";
import styles from "./Grouped.module.css";

type Payload = Schemas["PastOutputBody"];
type Tournament = Schemas["PastTournamentBody"];
type Event = Schemas["PastEventBody"];
type Result = Schemas["PastFencerBody"];

export interface PastProps {
  payload: Payload;
  groupBy: GroupBy;
  onGroupByChange: (next: GroupBy) => void;
  /**
   * One tracked fencer (decision 359): the tournament axis renders regardless
   * of the remembered preference (left untouched, so it survives fencer #2
   * arriving), the toggle is not rendered, and rows drop the constant name.
   */
  singleFencer?: boolean;
  /** The current query, owned by the route so it survives a re-fetch. */
  search: string;
  onSearchChange: (next: string) => void;
  searching?: boolean;
  /**
   * The list-scope control, or null (multi-list build item 3). A slot, for the
   * reasons Upcoming's own prop states — the same shape on both views, since
   * these two screens share a stylesheet and a grammar.
   */
  listScope?: ReactNode;
  /**
   * Whether this view's scope is NARROWER than every list the caller holds
   * (decision 521).
   *
   * It keeps the control on an EMPTY page, which is the one thing `chromeless`
   * below must not withhold: a scope naming a list with nothing in it emptied
   * this page, and the control is the only writer of that scope — stored per
   * view on the user row, never in the URL (501) — so dropping it left no way
   * back. 326 still holds where it was written: scope already every list, page
   * empty, no control.
   */
  scopeNarrowed?: boolean;
  locale?: string;
}

export function Past({
  payload,
  groupBy: rememberedGroupBy,
  onGroupByChange,
  singleFencer = false,
  search,
  onSearchChange,
  searching = false,
  listScope = null,
  scopeNarrowed = false,
  locale = dateLocale(),
}: PastProps) {
  const { t } = useTranslation();

  const groupBy: GroupBy = singleFencer ? "tournament" : rememberedGroupBy;

  const groups = regroup<Tournament, Event, Result>(payload.tournaments);

  const empty =
    groupBy === "tournament" ? groups.tournament.length === 0 : groups.fencer.length === 0;

  // Empty AND unsearched: nothing here, and not because of anything the user
  // typed. Only then are the search field and the grouping toggle controls over
  // a list that does not exist — a search that HAPPENED to match nothing keeps
  // both, or there is no way back out of the query (decision 326).
  const chromeless = empty && search.trim().length === 0;

  const headToggle =
    chromeless || singleFencer ? null : (
      <SegmentedToggle value={groupBy} onChange={onGroupByChange} />
    );
  /*
   * THE SCOPE CONTROL SURVIVES AN EMPTY PAGE WHEN THE SCOPE IS NARROW (521,
   * Kevin's fix), which is 326's own test applied rather than its default: a
   * control is withheld from an empty page because it acts on a list that is
   * not there, and this one acts on WHICH LISTS, which is exactly what emptied
   * the page. Withholding it was a trap with no exit — narrow the scope to a
   * list holding nothing and the page lost the only control that could widen
   * it again, for as long as that list stayed empty.
   *
   * The group-by toggle above keeps 326 whole, because regrouping nothing is
   * still nothing.
   */
  const headScope = chromeless && !scopeNarrowed ? null : listScope;

  return (
    <div className={styles.screen}>
      <div className={styles.head}>
        <h1 className={styles.h1}>{t("nav.past")}</h1>
        {/*
          THE TOGGLE IS A CONTROL OVER A LIST THAT IS NOT THERE (decision 326).
          On a genuinely empty Past it changes nothing visible, so it is an
          invitation to press something and watch nothing happen. Hidden with the
          search below, on the same condition and for the same reason.
        */}
        {/*
          AND HIDDEN AT ONE TRACKED FENCER (decision 359): both axes render
          identical content, so the toggle is a control with one outcome.
        */}
        {/*
          THE LIST SCOPE goes with it on 326's condition and stays at one fencer:
          which lists are in view is a different question from which axis the
          rows take. Upcoming's head says the same thing at more length; the two
          screens share this row's stylesheet and now share its shape.
        */}
        {headToggle || headScope ? (
          <div className={styles.headControls}>
            {headScope}
            {headToggle}
          </div>
        ) : null}
      </div>

      {/*
        The search field. §11: 'placeholder simply "Search"' — and the reason is
        given, which is why it is worth obeying literally: "long placeholders break
        at large font settings". A helpful "Search tournaments, fencers and
        events…" truncates to "Search tournam…" for the users who most need the
        larger text.
      */}
      {/*
        THE SEARCH SURVIVES A SEARCH THAT MATCHED NOTHING, and that distinction is
        the whole subtlety of hiding it (decision 326). "No results at all" and
        "no results for THIS query" look identical on screen and are opposite
        situations: hide the field in the second and there is no way to clear the
        query, so a stray keystroke strands the user on an empty page with no
        control to undo it. `chromeless` is therefore empty AND unsearched.
      */}
      {chromeless ? null : (
      <label className={styles.searchField}>
        <span className="tc-sr-only">{t("past.searchLabel")}</span>
        <input
          className={styles.search}
          type="search"
          name="past-search"
          autoComplete="off"
          placeholder={t("past.search")}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </label>
      )}

      {empty ? (
        <p className={styles.empty}>
          {search.trim() ? t("past.noMatches") : t("past.empty")}
        </p>
      ) : null}

      {/* Kept mounted while a new query is in flight, so the list does not blank
          out between keystrokes — the old results staying put reads as filtering,
          which is what it is. */}
      <div aria-busy={searching || undefined}>
        {groupBy === "tournament"
          ? groups.tournament.map(({ tournament, events }) => (
              <CollapsibleBand
                key={tournament.tournament_id}
                name={tournament.name}
                flag={<TournamentFlag country={tournament.country} />}
                line2={
                  <>
                    {/* City and country abroad, city and region at home (decision 776). */}
                    {formatLocation(tournament.city, tournament.region, tournament.country, locale) || null}
                    {/*
                      "3 fencers in 7 events" replaces the raw result count
                      (decision 326). It counts the same rows and says more: a
                      collapsed band's whole job is to tell you whether opening it
                      is worth it, and "12 results" does not distinguish one fencer
                      with twelve events from twelve fencers with one.
                    */}
                    {formatLocation(tournament.city, tournament.region, tournament.country, locale) ? " · " : null}
                    <TournamentSummary events={events} singleFencer={singleFencer} />
                  </>
                }
                dates={dateRange(tournament.start_date, tournament.end_date, locale)}
                // Expanded when a search is active: the user has already narrowed
                // to what they wanted, and making them open each group again would
                // be the screen refusing to answer the question it was asked.
                defaultOpen={search.trim().length > 0}
              >
                {events.map(({ event, fencers }) => (
                  <div key={event.event_id} className={styles.eventBlock}>
                    <EventSubHeader
                      attached
                      headingLevel={3}
                      name={event.event_name}
                      meta={
                        <span className="tc-num">{dateLabel(event.event_date, locale)}</span>
                      }
                      action={<ResultsPill event={event} />}
                    />
                    <Card attached dense>
                      {fencers.map((r) => (
                        <CardRow key={r.result_id} trailing={<Placement result={r} />}>
                          {/*
                            The constant name collapses at one tracked fencer
                            (decision 359): the placement is the row's content,
                            and the reader knows whose it is. No club either way
                            (decision 362) — the reader knows the club of a
                            fencer they track; identity surfaces keep it.
                          */}
                          {singleFencer ? null : (
                            <div className={styles.rowName}>{pastRowName(r, t("team.nameSeparator"))}</div>
                          )}
                          {/* A team's result names the team and its other
                              members (decision 714), at one tracked fencer too:
                              the team is what varies. */}
                          {r.team_name ? (
                            <TeamLine teamName={r.team_name} members={r.team_members} className={styles.rowMeta} />
                          ) : null}
                          {/* An earned rating is a real, celebrated outcome and
                              renders only when the source published one. */}
                          {r.earned_rating ? (
                            <div className={styles.rowMeta}>
                              {t("past.earnedRating", { rating: r.earned_rating })}
                            </div>
                          ) : null}
                        </CardRow>
                      ))}
                    </Card>
                  </div>
                ))}
              </CollapsibleBand>
            ))
          : groups.fencer.map((group) => (
              <CollapsibleBand
                key={group.key}
                // DISPLAY flips to First Last; the SORT stayed on the stored
                // surname-first string in regroup.byFencer, which is the whole
                // separation (decision 358). Bands remain in surname order.
                name={firstLast(group.name)}
                line2={<FencerSummary tournaments={group.tournaments} />}
                dates={null}
                defaultOpen={search.trim().length > 0}
              >
                {group.tournaments.map(({ tournament, events }) => (
                  <div key={tournament.tournament_id} className={styles.eventBlock}>
                    <EventSubHeader
                      attached
                      headingLevel={3}
                      name={tournament.name}
                      meta={
                        <span className="tc-num">
                          {dateRange(tournament.start_date, tournament.end_date, locale)}
                        </span>
                      }
                    />
                    <Card attached dense>
                      {events.map(({ event, entry }) => (
                        <CardRow
                          key={entry.result_id}
                          trailing={<Placement result={entry} />}
                        >
                          <div className={styles.rowName}>{event.event_name}</div>
                          {entry.team_name ? (
                            <TeamLine teamName={entry.team_name} members={entry.team_members} className={styles.rowMeta} />
                          ) : null}
                          <div className={styles.rowMeta}>
                            <span className="tc-num">
                              {dateLabel(event.event_date, locale)}
                            </span>
                          </div>
                        </CardRow>
                      ))}
                    </Card>
                  </div>
                ))}
              </CollapsibleBand>
            ))}
      </div>
    </div>
  );
}

/**
 * A placement (§11): "right-aligned, semibold, tabular: localized ordinal +
 * de-emphasized '(tie)' suffix. Weight carries the celebration — no color, no
 * serif."
 *
 * NO COLOUR is the rule worth restating, because a gold 1st is the obvious idea.
 * Red is interaction and amber is an honest state; a third meaning for colour here
 * would make all three weaker. And there is no denominator, for the same reason as
 * everywhere: field size is not reliably owned data (contract §8).
 */
/**
 * A result row's name: the person's, or on a team's result the tracked members
 * it is on screen for (decision 714), as Active and Upcoming name them, joined
 * with the language's separator (`team.nameSeparator`).
 */
function pastRowName(r: Result, separator: string): string {
  if (isTeamRow(r)) {
    const { tracked } = teamNames(r.team_members);
    if (tracked.length > 0) return tracked.join(separator);
  }
  return firstLast(r.name);
}

function Placement({ result }: { result: Result }) {
  const { t } = useTranslation();

  // "Excluded", muted (§5, §11). They competed, so they belong in the archive —
  // and a repeated exclusion pattern across a tracked history is an important cue
  // for this audience, which is why they are kept rather than dropped the way most
  // systems drop them.
  if (result.outcome === "excluded") {
    return <span className={styles.excluded}>{t("past.excluded")}</span>;
  }

  // Done, place unknown. Nothing rendered rather than a guess or a dash.
  if (result.place == null) return null;

  return (
    <span className={styles.place}>
      <span className="tc-num">
        {t("past.place", { count: result.place, ordinal: true })}
      </span>
      {result.tie ? <span className={styles.tie}>{t("active.outcome.tie")}</span> : null}
    </span>
  );
}

/**
 * The event's ONE results link, labelled by where it goes.
 *
 * The server picks the destination by source precedence — FTL, then AskFRED, never
 * USA Fencing (decision 318, invariant 7) — and sends which source won. The client
 * turns that into the LABEL, because §9's voice rule is that "link labels name the
 * destination kind and stay identical for identical destinations": FTL is always
 * "View" and AskFRED is always "Results", here and everywhere else in the app.
 *
 * So the label is not decoration on a fallback — it tells the user which site they
 * are about to land on, which is the difference between a properly rendered tableau
 * and a plain placement list.
 *
 * No destination, no pill. A URL in a payload is a promise (contract §0).
 */
function ResultsPill({ event }: { event: Event }) {
  if (!event.results_url) return null;

  return (
    <CapabilityPill
      kind={event.results_source === "askfred" ? "results" : "view"}
      href={event.results_url}
    />
  );
}

/**
 * §11's collapse pattern: the band is a full-width button.
 *
 * `aria-expanded` on a real <button>, and the chevron is `aria-hidden` — the
 * accessible name is the band's own text, so a screen reader announces "Trick or
 * Retreat ROC, collapsed" rather than naming a glyph.
 *
 * THE CONTENT IS UNMOUNTED WHEN CLOSED, not hidden with CSS. On a view that grows
 * forever this is the difference between a DOM of a hundred nodes and one of tens
 * of thousands; and a `display: none` subtree is skipped by assistive technology
 * anyway, so nothing is gained by keeping it.
 */
function CollapsibleBand({
  name,
  flag,
  line2,
  dates,
  defaultOpen,
  children,
}: {
  name: string;
  /** Rendered right after the name: a non-US tournament's flag (decision 776). */
  flag?: React.ReactNode;
  line2: React.ReactNode;
  dates: string | null;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  // Re-syncs when a search opens or closes the groups. Deliberately keyed on
  // defaultOpen alone: a user who has manually toggled a group keeps their choice
  // until the search state itself changes.
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  return (
    <section className={styles.group}>
      <button
        type="button"
        className={styles.collapseBand}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.collapseText}>
          {/*
            An h2 INSIDE the button. Unusual, and correct: §11 makes the whole band
            the control, and the tournament name is still the heading a screen
            reader should be able to navigate to. A heading wrapping the button
            instead would make the heading text and the control's name diverge.
          */}
          <h2 className={styles.collapseName}>
            {name}
            {flag}
            {dates ? <span className={`tc-num ${styles.collapseDates}`}> {dates}</span> : null}
          </h2>
          <span className={styles.collapseMeta}>{line2}</span>
        </span>

        {/*
          The red chevron: the interactive marker. Rotated by CSS on the expanded
          state, and the rotation is suppressed under prefers-reduced-motion —
          the glyph still turns, it just does not animate getting there.
        */}
        <span
          className={open ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
          aria-hidden="true"
        >
          ›
        </span>
      </button>

      {open ? <div className={styles.collapseBody}>{children}</div> : null}
    </section>
  );
}

/*
 * The band summaries (decision 326). Same shape as Upcoming's, over Past's types.
 *
 * Counted from what is IN THE BAND rather than from `tournament.result_count`,
 * which is deliberate: with a search active the payload is already filtered, so a
 * server-side total would describe rows the band does not contain. The number has
 * to agree with what opening it shows.
 */
function TournamentSummary({
  events,
  singleFencer = false,
}: {
  events: { event: Event; fencers: Result[] }[];
  singleFencer?: boolean;
}) {
  const { t } = useTranslation();
  const counts = tournamentCounts(events.map(({ fencers }) => ({ fencers })));

  // "1 fencer in 3 events" restates the screen's one constant per band when the
  // whole account tracks one fencer (decision 359); the event count varies.
  return (
    <span className="tc-num">
      {singleFencer
        ? t("summary.eventsOnly", { events: t("summary.events", { count: counts.events }) })
        : t("summary.fencersInEvents", {
            fencers: t("summary.fencers", { count: counts.fencers }),
            events: t("summary.events", { count: counts.events }),
          })}
    </span>
  );
}

function FencerSummary({
  tournaments,
}: {
  tournaments: { events: { event: Event; entry: Result }[] }[];
}) {
  const { t } = useTranslation();
  const counts = fencerCounts(tournaments);

  return (
    <span className="tc-num">
      {t("summary.tournamentsAndEvents", {
        tournaments: t("summary.tournaments", { count: counts.tournaments }),
        events: t("summary.events", { count: counts.events }),
      })}
    </span>
  );
}
